import isEqual from 'lodash/isEqual';

import { ERentalType } from '@/services/analytics/types';
import { getUrlParams } from '@/utility/queryParams';

import { IReview } from '../../types/core/reviews/id';
import { EProjectSection, trackEventV2 } from '../trackEventV2';
import {
  EReviewEventName,
  EReviewSource,
  IReviewRatedEvent,
  IReviewRecommendEvent,
  IReviewStepEvent,
  IReviewSubmitEvent,
  REVIEW_UPDATED_FIELD_COUNT_KEY,
  TReviewEditEvent,
  TReviewModalOpenedEvent,
} from './types';

export const trackReviewRated = (event: IReviewRatedEvent) => {
  const fromEmail = Boolean(getUrlParams(location.href)['token']);
  if (fromEmail) {
    event.source = EReviewSource.EMAIL;
  }
  trackEventV2(EReviewEventName.REVIEW_TRIP_RATED, EProjectSection.MARKETPLACE, event);
};

/** when user submit rating and experience */
export const trackReviewSubmit = (event: IReviewSubmitEvent) => {
  trackEventV2(EReviewEventName.RENTER_TRIP_REVIEWED, EProjectSection.MARKETPLACE, event);
};

export const trackReviewRecommendationSubmit = (event: IReviewRecommendEvent) => {
  trackEventV2(EReviewEventName.RENTER_TRIP_RECOMMENDED, EProjectSection.MARKETPLACE, event);
};

export const trackReviewEdit = (event: TReviewEditEvent) => {
  trackEventV2(EReviewEventName.RENTER_REVIEW_EDITED, EProjectSection.MARKETPLACE, event);
};

export const trackReviewModalOpened = (event: TReviewModalOpenedEvent) => {
  trackEventV2(EReviewEventName.REVIEW_MODAL_OPENED, EProjectSection.MARKETPLACE, event);
};

export const trackReviewStepCompleted = (event: IReviewStepEvent) => {
  trackEventV2(EReviewEventName.REVIEW_STEP_COMPLETED, EProjectSection.MARKETPLACE, event);
};

export const trackReviewStepAbandoned = (event: IReviewStepEvent) => {
  trackEventV2(EReviewEventName.REVIEW_STEP_ABANDONED, EProjectSection.MARKETPLACE, event);
};

/** when user submits full review */
export const trackReviewFinalSubmission = (
  newReviewData: Partial<IReview>,
  reviewData: Partial<IReview> | null,
) => {
  // review edit
  if (sessionStorage.getItem(REVIEW_UPDATED_FIELD_COUNT_KEY)) {
    const totalFieldChanged = countStepTwoReviewFieldChanged(newReviewData, reviewData);
    trackReviewEdit({
      bookingID: newReviewData.booking_id,
      ratingScore: newReviewData.score,
      experienceCharacterCount: newReviewData.text?.length || 0,
      totalImagesUploaded: newReviewData.images?.length || 0,
      communicationScore: newReviewData.communication_score,
      cleanlinessScore: newReviewData.cleanliness_score,
      mechanicalScore: newReviewData.mechanical_score,
      valueScore: newReviewData.value_score,
      listingAccuracyScore: newReviewData.listing_score,
      isRecommended: newReviewData.user_recommend,
      tags: newReviewData.tags?.map(tag => tag.slug) || [],
      totalChanges: totalFieldChanged,
      rentalType: (() => {
        if (reviewData?.rental_summary?.campsite_summary)
          return reviewData.rental_summary.campsite_summary.type || ERentalType.CAMPSITE;
        return ERentalType.RV;
      })() as ERentalType,
      isCampground: !!reviewData?.rental_summary?.campsite_summary,
    });
    return;
  }
  // new review
  trackReviewRecommendationSubmit({
    bookingID: newReviewData.booking_id,
    ratingScore: newReviewData.score,
    communicationScore: newReviewData.communication_score,
    cleanlinessScore: newReviewData.cleanliness_score,
    mechanicalScore: newReviewData.mechanical_score,
    valueScore: newReviewData.value_score,
    listingAccuracyScore: newReviewData.listing_score,
    isRecommended: newReviewData.user_recommend,
    tags: newReviewData.tags?.map(tag => tag.slug) || [],
    rentalType: (() => {
      if (reviewData?.rental_summary?.campsite_summary)
        return reviewData.rental_summary.campsite_summary.type || ERentalType.CAMPSITE;
      return ERentalType.RV;
    })() as ERentalType,
    isCampground: !!reviewData?.rental_summary?.campsite_summary,
  });
};
/**
 *
 * @returns count number of updated fields from /reviews/edit/{bookingId} page
 */
export const countStepOneReviewFieldChanged = (
  newReviewData: Partial<IReview>,
  reviewData: Partial<IReview> | null,
) => {
  /** it's a new review, so skip */
  if (!reviewData) {
    sessionStorage.removeItem(REVIEW_UPDATED_FIELD_COUNT_KEY);
    return;
  }
  let count = 0;

  if (newReviewData.score !== reviewData.score) count++;
  if (newReviewData.text !== reviewData.text) count++;
  if (!isEqual(newReviewData.images, reviewData.images)) count++;

  sessionStorage.setItem(REVIEW_UPDATED_FIELD_COUNT_KEY, count.toString());
};

/**
 *
 * @returns count number of updated fields from /reviews/{reviewId}/details page
 */
const countStepTwoReviewFieldChanged = (
  newReviewData: Partial<IReview>,
  reviewData: Partial<IReview> | null,
) => {
  /** it's a new review */
  if (!sessionStorage.getItem(REVIEW_UPDATED_FIELD_COUNT_KEY) || !reviewData) {
    return;
  }
  let count = 0;

  if (newReviewData.communication_score !== reviewData.communication_score) count++;
  if (newReviewData.cleanliness_score !== reviewData.cleanliness_score) count++;
  if (newReviewData.mechanical_score !== reviewData.mechanical_score) count++;
  if (newReviewData.value_score !== reviewData.value_score) count++;
  if (newReviewData.listing_score !== reviewData.listing_score) count++;
  if (newReviewData.user_recommend !== reviewData.user_recommend) count++;
  if (!isEqual(newReviewData.tags, reviewData.rental_tags)) count++;

  return count;
};

export const countReviewFieldChanged = (
  newReviewData: Partial<IReview>,
  reviewData: Partial<IReview> | null,
) => {
  if (!reviewData) return 0;
  let count = 0;
  if (newReviewData.score !== reviewData.score) count++;
  if (newReviewData.text !== reviewData.text) count++;
  if (!isEqual(newReviewData.images, reviewData.images)) count++;
  if (newReviewData.communication_score !== reviewData.communication_score) count++;
  if (newReviewData.cleanliness_score !== reviewData.cleanliness_score) count++;
  if (newReviewData.mechanical_score !== reviewData.mechanical_score) count++;
  if (newReviewData.value_score !== reviewData.value_score) count++;
  if (newReviewData.listing_score !== reviewData.listing_score) count++;
  if (newReviewData.user_recommend !== reviewData.user_recommend) count++;
  if (!isEqual(newReviewData.tags, reviewData.rental_tags)) count++;
  return count;
};
